/**
 * Client-side mirror of the API's role-to-permission compilation. Must be
 * kept in sync with `apps/api/src/auth/permissions.ts`. A follow-up change
 * can share this via the `@signage42/shared-types` package.
 */
export type Role = 'owner' | 'admin' | 'editor' | 'scheduler' | 'viewer' | 'member';

export const ALL_PERMISSIONS = [
  'organization:read',
  'organization:update',
  'workspace:read',
  'workspace:write',
  'workspace:delete',
  'member:read',
  'member:write',
  'rolegrant:read',
  'rolegrant:write',
  'group:read',
  'group:write',
  'media:read',
  'media:write',
  'media:delete',
  'layout:read',
  'layout:write',
  'layout:delete',
  'layout:embed-html',
  'playlist:read',
  'playlist:write',
  'playlist:delete',
  'schedule:read',
  'schedule:write',
  'schedule:delete',
  'schedule:preview',
  'screen:read',
  'screen:write',
  'screen:delete',
  'device:read',
  'device:pair',
  'device:revoke',
  'device:control',
  'device:update',
  'approval:submit',
  'approval:approve',
  'approval:publish',
  'approval:archive',
  'approval:log:read',
  'performance-feed:read',
  'performance-feed:write',
  'performance-feed:sync',
  'performance:read',
  'performance:override',
  'performance-rule:read',
  'performance-rule:write',
  'signal:read',
  'signal:trigger',
  'signal:emergency',
] as const;

export type Permission = (typeof ALL_PERMISSIONS)[number];

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  owner: [...ALL_PERMISSIONS],
  admin: [
    'organization:read',
    'organization:update',
    'workspace:read',
    'workspace:write',
    'workspace:delete',
    'member:read',
    'member:write',
    'rolegrant:read',
    'rolegrant:write',
    'group:read',
    'group:write',
    'media:read',
    'media:write',
    'media:delete',
    'layout:read',
    'layout:write',
    'layout:delete',
    'layout:embed-html',
    'playlist:read',
    'playlist:write',
    'playlist:delete',
    'schedule:read',
    'schedule:write',
    'schedule:delete',
    'schedule:preview',
    'screen:read',
    'screen:write',
    'screen:delete',
    'device:read',
    'device:pair',
    'device:revoke',
    'device:control',
    'device:update',
    'approval:submit',
    'approval:approve',
    'approval:publish',
    'approval:archive',
    'approval:log:read',
    'performance-feed:read',
    'performance-feed:write',
    'performance-feed:sync',
    'performance:read',
    'performance:override',
    'performance-rule:read',
    'performance-rule:write',
    'signal:read',
    'signal:trigger',
    'signal:emergency',
  ],
  editor: [
    'organization:read',
    'workspace:read',
    'member:read',
    'media:read',
    'media:write',
    'media:delete',
    'layout:read',
    'layout:write',
    'playlist:read',
    'playlist:write',
    'playlist:delete',
    'screen:read',
    'device:control',
    'approval:submit',
    'approval:log:read',
    'performance:read',
    'performance:override',
    'signal:read',
  ],
  scheduler: [
    'organization:read',
    'workspace:read',
    'member:read',
    'media:read',
    'layout:read',
    'playlist:read',
    'schedule:read',
    'schedule:write',
    'schedule:delete',
    'schedule:preview',
    'screen:read',
    'device:read',
    'device:control',
    'approval:submit',
    'approval:approve',
    'approval:publish',
    'approval:log:read',
    'performance-feed:sync',
    'performance:read',
    'performance-rule:read',
    'performance-rule:write',
    'signal:read',
    'signal:trigger',
  ],
  // UG10: base identity of a group-scoped user. Content access comes from group
  // grants and is evaluated server-side per row, so the client sees only the
  // group-conferred permissions the session's groups actually grant (see
  // GROUP_GRANT_PERMISSIONS below).
  member: ['organization:read', 'layout:read'],
  viewer: [
    'organization:read',
    'workspace:read',
    'media:read',
    'layout:read',
    'playlist:read',
    'schedule:read',
    'schedule:preview',
    'screen:read',
    'device:read',
    'approval:log:read',
    'performance:read',
    'signal:read',
  ],
};

export interface RoleGrant {
  organizationId: string;
  workspaceId: string | null;
  role: Role;
}

export function computePermissions(
  grants: readonly RoleGrant[],
  target: { organizationId: string; workspaceId?: string | null },
): Set<Permission> {
  const effective = new Set<Permission>();
  for (const g of grants) {
    if (g.organizationId !== target.organizationId) continue;
    if (g.workspaceId !== null && g.workspaceId !== (target.workspaceId ?? null)) continue;
    for (const p of ROLE_PERMISSIONS[g.role]) effective.add(p);
  }
  return effective;
}

/**
 * Mirror of the API's group-conferred permission set (UG3). Holding at least
 * one folder-subtree grant confers these on top of the role bundle; which rows
 * they reach is decided server-side by the granted folder closure, so the client
 * uses them only to decide what to render.
 */
export const GROUP_GRANT_PERMISSIONS: readonly Permission[] = [
  'organization:read',
  'layout:read',
  'media:read',
  'media:write',
  'media:delete',
  'playlist:read',
  'playlist:write',
  'playlist:delete',
  'approval:submit',
  'approval:log:read',
];

/**
 * UG3 — is content access supplied only by group grants? Any org-wide content
 * role carries `media:read` or `playlist:read` and sees the whole library; a
 * principal carrying neither is folder-scoped, and the nav drops everything but
 * Media and Playlists (UG9).
 */
export function isGroupScopedPermissionSet(rolePermissions: ReadonlySet<Permission>): boolean {
  return !rolePermissions.has('media:read') && !rolePermissions.has('playlist:read');
}
